import React, { useState, useEffect } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './SpeakerManagement.module.css';
import { PlusIcon, MicIcon, FilterIcon } from '../../components/Icons';

function SpeakerManagement() {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    genders: [],
    ages: [],
    languages: []
  });
  const [filters, setFilters] = useState({
    gender: '',
    age: '',
    language: '',
    is_multi_languages: ''
  });
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    gender: '女',
    age: '青',
    api_name: '',
    is_multi_languages: false,
    language: 'zh-CN',
    tag: ''
  });

  useEffect(() => {
    fetchSpeakers();
    fetchFilterOptions();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSpeakers();
  }, [filters]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.gender) params.gender = filters.gender;
      if (filters.age) params.age = filters.age;
      if (filters.language) params.language = filters.language;
      if (filters.is_multi_languages !== '') params.is_multi_languages = filters.is_multi_languages;
      
      const response = await api.get('/speakers/', { params });
      setSpeakers(response.data);
    } catch (error) {
      console.error('获取说话人列表失败:', error);
      // alert('获取说话人列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/speakers/options/filters');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/speakers/stats/count');
      setStats(response.data);
    } catch (error) {
      console.error('获取统计信息失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingSpeaker(null);
    setFormData({
      name: '',
      gender: '女',
      age: '青',
      api_name: '',
      is_multi_languages: false,
      language: 'zh-CN',
      tag: ''
    });
    setShowModal(true);
  };

  const handleEdit = (speaker) => {
    setEditingSpeaker(speaker);
    setFormData({
      name: speaker.name,
      gender: speaker.gender,
      age: speaker.age,
      api_name: speaker.api_name,
      is_multi_languages: speaker.is_multi_languages,
      language: speaker.language,
      tag: speaker.tag || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (speaker) => {
    if (window.confirm(`确定要删除说话人 "${speaker.name}" 吗？`)) {
      try {
        await api.delete(`/speakers/${speaker.id}`);
        fetchSpeakers();
        fetchStats();
        // alert('说话人删除成功');
      } catch (error) {
        console.error('删除说话人失败:', error);
        // alert('删除说话人失败');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      // alert('请输入说话人名称');
      return;
    }
    if (!formData.api_name.trim()) {
      // alert('请输入API名称');
      return;
    }

    try {
      if (editingSpeaker) {
        await api.put(`/speakers/${editingSpeaker.id}`, formData);
        // alert('说话人更新成功');
      } else {
        await api.post('/speakers/', formData);
        // alert('说话人创建成功');
      }
      
      setShowModal(false);
      fetchSpeakers();
      fetchStats();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('保存说话人失败:', error);
      alert(error.response?.data?.detail || '保存说话人失败');
    }
  };

  const clearFilters = () => {
    setFilters({
      gender: '',
      age: '',
      language: '',
      is_multi_languages: ''
    });
  };

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'name', label: '名称' },
    { 
      key: 'gender', 
      label: '性别',
      width: '80px',
      render: (value) => (
        <span className={`${styles.badge} ${styles[`badge${value}`]}`}>
          {value}
        </span>
      )
    },
    { 
      key: 'age', 
      label: '年龄段',
      width: '80px',
      render: (value) => (
        <span className={styles.ageBadge}>
          {value}
        </span>
      )
    },
    { key: 'api_name', label: 'API名称' },
    { 
      key: 'language', 
      label: '语言',
      width: '100px',
      render: (value) => (
        <span className={styles.langBadge}>
          {value}
        </span>
      )
    },
    { 
      key: 'is_multi_languages', 
      label: '多语言',
      width: '80px',
      render: (value) => (
        <span className={value ? styles.multiYes : styles.multiNo}>
          {value ? '✓' : '✗'}
        </span>
      )
    },
    { 
      key: 'tag', 
      label: '标签',
      render: (value) => (
        <span className={styles.tags}>
          {value || '-'}
        </span>
      )
    }
  ];

  const hasActiveFilters = filters.gender || filters.age || filters.language || filters.is_multi_languages !== '';

  return (
    <div className={styles.speakerManagement}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>说话人管理</h1>
          <p className={styles.subtitle}>管理语音合成说话人信息</p>
        </div>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加说话人
        </button>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <MicIcon size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.total_speakers}</div>
              <div className={styles.statLabel}>总说话人数</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>🌍</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.multi_languages_count}</div>
              <div className={styles.statLabel}>多语言支持</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>👥</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {Object.keys(stats.gender_distribution || {}).length}
              </div>
              <div className={styles.statLabel}>性别分类</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>🗣️</div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>
                {Object.keys(stats.language_distribution || {}).length}
              </div>
              <div className={styles.statLabel}>语言类型</div>
            </div>
          </div>
        </div>
      )}

      {/* 筛选器 */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <div className={styles.filterTitle}>
            <FilterIcon size={18} />
            筛选条件
          </div>
          {hasActiveFilters && (
            <button className={styles.clearBtn} onClick={clearFilters}>
              清除筛选
            </button>
          )}
        </div>
        <div className={styles.filters}>
          <select
            className={styles.filterSelect}
            value={filters.gender}
            onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
          >
            <option value="">所有性别</option>
            {filterOptions.genders.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>

          <select
            className={styles.filterSelect}
            value={filters.age}
            onChange={(e) => setFilters({ ...filters, age: e.target.value })}
          >
            <option value="">所有年龄段</option>
            {filterOptions.ages.map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>

          <select
            className={styles.filterSelect}
            value={filters.language}
            onChange={(e) => setFilters({ ...filters, language: e.target.value })}
          >
            <option value="">所有语言</option>
            {filterOptions.languages.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>

          <select
            className={styles.filterSelect}
            value={filters.is_multi_languages}
            onChange={(e) => setFilters({ ...filters, is_multi_languages: e.target.value })}
          >
            <option value="">多语言支持</option>
            <option value="true">支持</option>
            <option value="false">不支持</option>
          </select>
        </div>
      </div>

      <AdminTable
        data={speakers}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无说话人数据"
      />

      {showModal && (
        <AdminModal
          title={editingSpeaker ? '编辑说话人' : '添加说话人'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="medium"
        >
          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>名称 *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如：小美"
                  required
                  autoFocus
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>API名称 *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.api_name}
                  onChange={(e) => setFormData({ ...formData, api_name: e.target.value })}
                  placeholder="例如：zh-CN-XiaomeiNeural"
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>性别</label>
                <select
                  className={styles.select}
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                >
                  <option value="女">女</option>
                  <option value="男">男</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>年龄段</label>
                <select
                  className={styles.select}
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                >
                  <option value="少">少</option>
                  <option value="青">青</option>
                  <option value="中">中</option>
                  <option value="老">老</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>语言</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  placeholder="例如：zh-CN, en-US"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.is_multi_languages}
                  onChange={(e) => setFormData({ ...formData, is_multi_languages: e.target.checked })}
                />
                <span>支持多语言</span>
              </label>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>标签</label>
              <input
                type="text"
                className={styles.input}
                value={formData.tag}
                onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                placeholder="例如：活泼、温柔、专业"
              />
              <div className={styles.inputHint}>
                多个标签用逗号分隔
              </div>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default SpeakerManagement;
